/** @prettier */

import {useEffect, useState} from 'react';
import {utcToZonedTime} from 'date-fns-tz';
import {differenceInCalendarDays, format, sub} from 'date-fns';

//API import
import {getStatesSitesRt} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {GraphFormType, GraphDataRange} from '../../Type/TypeDef';

import {graph} from '../../define';

type UseRTChangeStartEndDateArgType = {
  siteID: string | undefined; //サイトID
  graphStartDate: string; //グラフ開始日
  graphEndDate: string; //グラフ最終日
  graphType: GraphFormType; //グラフの種類
  timeZone: string; //タイムゾーン
  dateRange: GraphDataRange; //グラフの範囲
  selectedItem:
    | (keyof apiIF.resParameters.rtOUStates)[]
    | (keyof apiIF.resParameters.rtIUStates)[]; //表示項目
};
export const useRTChangeStartEndDate = ({
  siteID,
  graphStartDate,
  graphEndDate,
  graphType,
  timeZone,
  dateRange,
  selectedItem,
}: UseRTChangeStartEndDateArgType) => {
  const [apiWait, setAPIWait] = useState(false); //API待機中判定
  const [trendDisable, setTrendDisable] = useState(false); //トレンドグラフ選択可否
  const [powerDisable, setPowerDisable] = useState(false); //電力グラフ選択可否
  const [graphDispError, setGraphDispError] = useState(false); //グラフ表示エラー判定
  const [csvFileNameDate, setCSVFileNameDate] = useState(''); //CSVファイルの名前（日付部分）
  const [rtStateData, setRTStateData] =
    useState<apiIF.res.getStatesSitesRt | null>(null); //熱源機状態データ

  //グラフボタン押下、他ユニットのグラフを表示、カレンダ日付変更に日時を更新し、グラフ描画用データを取得する
  const fieldsParam = selectedItem.join(',');

  useEffect(() => {
    if (siteID === undefined || graphStartDate === '' || graphEndDate === '') {
      return;
    }
    setAPIWait(true);
    setTrendDisable(true);
    setPowerDisable(true);

    //APIに渡すクエリの決定
    let fields = '';
    if (graphType === 'line') fields = fieldsParam;
    if (graphType === 'bar') fields = 'power-consumption';
    if (graphType === 'barLineMixed') fields = 'energy-amount,cop';
    const afterDate = graphStartDate; //APIに送るデータ取得開始日
    const beforeDate = graphEndDate; //APIに送るデータ取得終了日

    let everyMinute; //データ取得間隔分数
    let maxResult; //最大データ取得数
    let csvTitle; // csvに表示する日時
    switch (dateRange) {
      case 'day':
        everyMinute =
          graphType === 'line'
            ? graph.DAY_LINE_DATE_RANGE
            : graph.DAY_BAR_DATE_RANGE;
        maxResult =
          graphType === 'line'
            ? graph.DAY_LINE_API_MAX_RESULT
            : graph.DAY_BAR_API_MAX_RESULT;
        csvTitle = format(utcToZonedTime(graphStartDate, timeZone), 'yyyyMMdd');
        break;
      case 'week':
        everyMinute =
          graphType === 'line'
            ? graph.WEEK_LINE_DATE_RANGE
            : graph.WEEK_BAR_DATE_RANGE;
        maxResult =
          graphType === 'line'
            ? graph.WEEK_LINE_API_MAX_RESULT
            : graph.WEEK_BAR_API_MAX_RESULT;
        //1週間表示のcsvファイルに出力する日付情報を作成
        const tempCSVTitleStart = format(
          utcToZonedTime(graphStartDate, timeZone),
          'yyyyMMdd'
        );
        const tempCSVTitleEnd = format(
          utcToZonedTime(sub(new Date(graphEndDate), {days: 1}), timeZone),
          'yyyyMMdd'
        );
        csvTitle = `${tempCSVTitleStart}_${tempCSVTitleEnd}`;
        break;
      case 'month':
        everyMinute = graph.MONTH_BAR_DATE_RANGE;
        maxResult = sub(new Date(beforeDate), {days: 1}).getDate();
        const tempMonthCSVTitleStart = format(
          utcToZonedTime(graphStartDate, timeZone),
          'yyyyMM'
        );
        csvTitle = `${tempMonthCSVTitleStart}`;
        break;
      case 'year':
        everyMinute = graph.YEAR_BAR_DATE_RANGE;
        maxResult = graph.YEAR_BAR_API_MAX_RESULT;
        //1年表示のcsvファイルに出力する日付情報を作成
        const yearStart = format(
          utcToZonedTime(graphStartDate, timeZone),
          'yyyy'
        );
        csvTitle = `${yearStart}`;
        break;
      case 'tenYear':
        everyMinute = graph.TEN_YEAR_BAR_DATE_RANGE;
        maxResult = graph.TEN_YEAR_BAR_API_MAX_RESULT;
        //10年表示のcsvファイルに出力する日付情報を作成
        const tenYearStart = format(
          utcToZonedTime(graphStartDate, timeZone),
          'yyyy'
        );
        //10年表示のcsvファイルに出力するグラフの終了年を作成
        const tenYearEnd = format(
          utcToZonedTime(sub(new Date(graphEndDate), {days: 1}), timeZone),
          'yyyy'
        );
        csvTitle = `${tenYearStart}_${tenYearEnd}`;
    }

    //グラフボタンと日付変更が同時に発生した場合、useEffectが2回走り、APIの完了タイミングによって
    //day選択時にweekの結果を表示しようとするので、これを防ぐため下記条件を実装
    const distance = differenceInCalendarDays(
      new Date(graphEndDate),
      new Date(graphStartDate)
    );
    if (
      (dateRange === 'day' && distance < 2) ||
      (dateRange === 'week' && distance >= 2 && distance <= 7) ||
      (dateRange === 'month' && distance > 7 && distance <= 31) ||
      (dateRange === 'year' && distance > 31 && distance <= 366) ||
      (dateRange === 'tenYear' && distance > 366 && distance <= 3653)
    ) {
      setCSVFileNameDate(csvTitle);
      setGraphDispError(false);
      getStatesSitesRt(siteID, {
        'fields': fields,
        'after-time': afterDate,
        'before-time': beforeDate,
        'every-minute': everyMinute,
        'max-result': maxResult,
      })
        .then(response => {
          setRTStateData(response);
        })
        .catch(response => {
          setRTStateData(null);
          setGraphDispError(true);
          setTrendDisable(false);
          setPowerDisable(false);
        })
        .finally(() => {
          setAPIWait(false);
        });
    }
  }, [graphType, graphStartDate, graphEndDate, fieldsParam]);
  return {
    rtStateData,
    csvFileNameDate,
    trendDisable,
    powerDisable,
    graphDispError,
    apiWait,
  };
};
